import { gql, useQuery } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import Button from '@orn/shared-web/components/Button/Button';
import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import Input from '@orn/shared-web/components/Input';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import {
  AlertTriangleIcon,
  CloseIcon,
  LargeSearchIcon,
  RecurringIcon,
  SwitchHorizontalIcon,
} from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import {
  CalloutPriority,
  PaginationDirection,
  TransactionAmountType,
  TransactionSearchType,
  TransactionSortKeys,
  type TransactionFilters,
} from '../../../__generated__/globalTypes';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import TransactionSlideout from '../../slideouts/TransactionSlideout';
import {
  AccountPrimaryElement,
  AccountSecondaryElement,
  MerchantPrimaryElement,
  TransactionPairTooltip,
} from '../elements';
import { AccountFilterDropdown, AmountTypeFilterDropdown } from '../filters';
import { EmptyTableState, ErrorTableState, LoadingTableState, type IEmptyTableStateProps } from '../helpers';
import useHasTransactionsForEmptyState from '../hooks/useHasTransactionsForEmptyState';

import type {
  GetTransactionsForTableQuery,
  GetTransactionsForTableQueryVariables,
} from './__generated__/TransactionsTable.gql';

const PAGE_SIZE = 30;

// Minimal fragment for table display only
const TransactionTableRowFragment = gql`
  fragment TransactionTableRow on Transaction {
    id
    amount
    date
    description
    isPending
    pairedTransaction {
      id
      date
      amount
      financialAccount {
        id
        subtype
        mask
        name
        ...EntityAvatar
      }
    }
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
    recurringTransaction {
      id
      description
    }
    callout {
      id
      priority
    }
  }
  ${Entity.Avatar.Fragment}
`;

const GET_TRANSACTIONS_FOR_TABLE = gql`
  query GetTransactionsForTable(
    $pagination: PaginationInput!
    $filters: TransactionFilters!
    $sortKey: TransactionSortKeys!
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...TransactionTableRow
        }
      }
    }
  }
  ${TransactionTableRowFragment}
`;

export enum TxnTableElementType {
  Account = 'Account',
  Merchant = 'Merchant',
}

export enum TxnTableFilterTypes {
  Account = 'Account',
  AmountType = 'AmountType',
}

type Transaction = NonNullable<
  NonNullable<GetTransactionsForTableQuery['getTransactionsForUser']>['edges']
>[number]['node'];

interface IBaseTransactionsTableProps {
  transactions: Transaction[];
  primaryElement: TxnTableElementType;
  secondaryElement?: TxnTableElementType;
  allowedFilters?: TxnTableFilterTypes[];
  dateRange?: CalendarDateRange;
  selectedAccountIds: string[];
  onAccountChange: (accountIds: string[]) => void;
  selectedTransactionAmountTypes: TransactionAmountType[];
  onTransactionAmountTypeChange: (types: TransactionAmountType[]) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoadingInitial?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  hasNextPage: boolean;
  onLoadMore: () => Promise<void>;
  emptyStateProps?: IEmptyTableStateProps;
}

export const BaseTransactionsTable = ({
  transactions,
  primaryElement,
  secondaryElement,
  allowedFilters,
  dateRange,
  selectedAccountIds,
  onAccountChange,
  selectedTransactionAmountTypes,
  onTransactionAmountTypeChange,
  searchQuery,
  onSearchQueryChange,
  isLoadingInitial = false,
  isLoadingMore = false,
  hasError = false,
  hasNextPage,
  onLoadMore,
  emptyStateProps,
}: IBaseTransactionsTableProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);

  const allowedFiltersArray = React.useMemo(() => allowedFilters ?? [], [allowedFilters]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore: hasNextPage,
    isLoading: isLoadingMore,
    enabled: !isCollapsed && !isLoadingInitial,
    threshold: 1,
  });

  const canClearFilters = React.useMemo(() => {
    if (allowedFiltersArray.length === 0) {
      return false;
    }

    return (
      (allowedFiltersArray.includes(TxnTableFilterTypes.Account) && selectedAccountIds.length > 0) ||
      (allowedFiltersArray.includes(TxnTableFilterTypes.AmountType) && selectedTransactionAmountTypes.length > 0)
    );
  }, [allowedFiltersArray, selectedAccountIds.length, selectedTransactionAmountTypes.length]);

  const handleClearFilters = React.useCallback(() => {
    if (allowedFiltersArray.includes(TxnTableFilterTypes.Account)) {
      onAccountChange([]);
    }
    if (allowedFiltersArray.includes(TxnTableFilterTypes.AmountType)) {
      onTransactionAmountTypeChange([]);
    }
  }, [allowedFiltersArray, onAccountChange, onTransactionAmountTypeChange]);
  const filterElements = [
    allowedFiltersArray.includes(TxnTableFilterTypes.Account) ? (
      <AccountFilterDropdown key="account-filter" selectedIds={selectedAccountIds} onSelect={onAccountChange} />
    ) : null,
    allowedFiltersArray.includes(TxnTableFilterTypes.AmountType) ? (
      <AmountTypeFilterDropdown
        key="amount-type-filter"
        selectedTypes={selectedTransactionAmountTypes}
        onSelect={onTransactionAmountTypeChange}
      />
    ) : null,
  ].filterNullish();

  const getPrimaryElement = (transaction: Transaction) => {
    if (primaryElement === TxnTableElementType.Account) {
      return <AccountPrimaryElement financialAccount={transaction.financialAccount} />;
    } else if (primaryElement === TxnTableElementType.Merchant) {
      return <MerchantPrimaryElement merchant={transaction.merchant} fallbackDescription={transaction.description} />;
    }
    return undefined;
  };

  const getSecondaryElementLabel = () => {
    if (secondaryElement === TxnTableElementType.Account) {
      return 'Account';
    }
    return 'Merchant';
  };

  const getSecondaryElement = (transaction: Transaction) => {
    if (secondaryElement === TxnTableElementType.Account) {
      return <AccountSecondaryElement financialAccount={transaction.financialAccount} />;
    }
    return undefined;
  };

  const dateRangeText = useDateRangeDisplayText(dateRange, 'allTime');
  const isMdOrUp = useBreakpoint('md');
  const isLgOrUp = useBreakpoint('lg');
  const tableSize = isLgOrUp ? 'md' : 'sm';
  const isTransactionSlideoutOpen = selectedTransactionId !== null;

  return (
    <TableCard.Root className="min-w-0 border-none" size={tableSize}>
      <TableCard.Header
        title="Transactions"
        subtitle={dateRangeText}
        trailingInline
        contentTrailing={
          <div className="flex min-w-0 max-w-full flex-1 items-center gap-4">
            <Input
              icon={LargeSearchIcon}
              placeholder="Search"
              size="sm"
              value={searchQuery}
              onChange={onSearchQueryChange}
              wrapperClassName="!rounded-full min-w-0"
              inputClassName="!h-9"
              className="lg:w-68 min-w-0 flex-1"
              aria-label="Search transactions"
            />
            <div className="shrink-0">
              <CollapseButton isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            </div>
          </div>
        }
      />
      {!isCollapsed && (
        <>
          {filterElements.length > 0 && (
            <div className="border-tertiary bg-primary flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5">
              <div className="flex flex-wrap items-center gap-2">{filterElements}</div>
              {canClearFilters && (
                <Button size="sm" color="link-gray" iconTrailing={CloseIcon} onClick={handleClearFilters}>
                  Clear all
                </Button>
              )}
            </div>
          )}
          {isLoadingInitial && <LoadingTableState />}
          {!isLoadingInitial && hasError && <ErrorTableState />}
          {!isLoadingInitial && !hasError && transactions.length === 0 && emptyStateProps && (
            <EmptyTableState {...emptyStateProps} />
          )}
          {!isLoadingInitial && !hasError && transactions.length > 0 && (
            <Table aria-label="Transactions table" className="w-full">
              {transactions.length > 0 && (
                <Table.Header>
                  <Table.Head label="Date" className="pr-4" isRowHeader />
                  <Table.Head label="Name" className="min-w-0" />
                  {isLgOrUp && secondaryElement && <Table.Head className="px-2" label={getSecondaryElementLabel()} />}
                  {isMdOrUp && <Table.Head label="" />}
                  <Table.Head label="Amount" className="flex justify-end" />
                </Table.Header>
              )}
              <Table.Body dependencies={[isMdOrUp, isLgOrUp]} items={transactions}>
                {(transaction) => (
                  <Table.Row
                    key={transaction.id}
                    className={'h-16 cursor-pointer'}
                    onClick={() => {
                      setSelectedTransactionId(transaction.id);
                    }}
                  >
                    <Table.Cell className="w-0 pr-4">
                      <div className="text-primary text-base font-medium">{formatCalendarDate(transaction.date)}</div>
                    </Table.Cell>
                    <Table.Cell
                      className={cx(
                        'w-full min-w-0 max-w-0 px-4 py-3.5',
                        primaryElement === TxnTableElementType.Account && 'py-2.5'
                      )}
                    >
                      <div className="max-w-full truncate">{getPrimaryElement(transaction)}</div>
                    </Table.Cell>
                    {isLgOrUp && secondaryElement && (
                      <Table.Cell className="w-28 px-2 py-4">{getSecondaryElement(transaction)}</Table.Cell>
                    )}
                    {isMdOrUp && (
                      <Table.Cell className="w-0 px-2 py-4">
                        <div className="flex items-center justify-center gap-1">
                          {transaction.pairedTransaction?.id && (
                            <TransactionPairTooltip pair={transaction.pairedTransaction}>
                              <SwitchHorizontalIcon className="text-quaternary size-5" />
                            </TransactionPairTooltip>
                          )}
                          {transaction.recurringTransaction && (
                            <Tooltip title={`Recurring - ${transaction.recurringTransaction.description}`}>
                              <TooltipTrigger>
                                <RecurringIcon className="text-quaternary size-5" />
                              </TooltipTrigger>
                            </Tooltip>
                          )}
                          {transaction.callout && (
                            <Tooltip title="Unusual transaction">
                              <TooltipTrigger>
                                <AlertTriangleIcon
                                  className={cx(
                                    'size-5',
                                    transaction.callout.priority === CalloutPriority.P0
                                      ? 'text-utility-error-500'
                                      : 'text-utility-warning-500'
                                  )}
                                />
                              </TooltipTrigger>
                            </Tooltip>
                          )}
                        </div>
                      </Table.Cell>
                    )}
                    <Table.Cell className={cx('w-0 pl-4', transaction.isPending && 'pt-2.75 pb-2.75')}>
                      <div className="flex flex-col items-end">
                        <div
                          className={cx(
                            'text-base font-medium',
                            Number(transaction.amount) < 0 ? 'text-success-primary' : 'text-primary'
                          )}
                        >
                          {toCurrency(Math.abs(Number(transaction.amount)), {
                            showSign: Number(transaction.amount) < 0,
                          })}
                        </div>
                        {transaction.isPending && <div className="text-tertiary text-xs italic">Pending</div>}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
          {!isLoadingInitial && !hasError && hasNextPage && (
            <div className="bg-secondary_subtle border-secondary flex items-center justify-center gap-3 border-t px-6 py-4">
              <div ref={sentinelRef} className="flex items-center justify-center">
                <LoadingIndicator size="sm" />
              </div>
            </div>
          )}
        </>
      )}
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={isTransactionSlideoutOpen}
        onClose={() => setSelectedTransactionId(null)}
      />
    </TableCard.Root>
  );
};

interface ITransactionsTableProps {
  /* Filters */
  financialAccountIds?: string[];
  merchantIds?: string[];
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (range: CalendarDateRange) => void;
  excludeRecurring?: boolean;

  /* Element Display */
  primaryElement: TxnTableElementType;
  secondaryElement?: TxnTableElementType;

  hideEmpty?: boolean;
}

const TransactionsTable = ({
  financialAccountIds,
  merchantIds,
  dateRange,
  onDateRangeChange,
  excludeRecurring,
  primaryElement,
  secondaryElement,
  hideEmpty = false,
}: ITransactionsTableProps) => {
  // Compute allowedFilters based on which filters don't have default values
  // Note: DateRange filter is not shown in the table filter bar - date range is controlled
  // at the pane level via URL-based date range (see DateRangeProvider).
  // When no dateRangeProp is passed, all transactions are shown (all time).
  const allowedFilters = React.useMemo(() => {
    const computed: TxnTableFilterTypes[] = [];

    if (financialAccountIds === undefined) {
      computed.push(TxnTableFilterTypes.Account);
    }

    // AmountType never has a default, so always allow it
    computed.push(TxnTableFilterTypes.AmountType);

    return computed;
  }, [financialAccountIds]);

  const [selectedAccountIds, setSelectedAccountIds] = React.useState<string[]>(financialAccountIds ?? []);
  const [selectedTransactionAmountTypes, setSelectedTransactionAmountTypes] = React.useState<TransactionAmountType[]>(
    []
  );

  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 250);

  const filters = React.useMemo<TransactionFilters>(() => {
    const filter: TransactionFilters = {};

    if (selectedAccountIds.length > 0) {
      filter.financialAccountIds = selectedAccountIds;
    }

    if (merchantIds && merchantIds.length > 0) {
      filter.merchantIds = merchantIds;
    }

    if (selectedTransactionAmountTypes.length > 0) {
      const amountType = selectedTransactionAmountTypes[0];
      if (amountType === TransactionAmountType.Credit || amountType === TransactionAmountType.Debit) {
        filter.transactionAmountType = amountType;
      }
    }

    if (dateRange) {
      filter.startDate = dateRange.start.toString() as unknown as CalendarDate;
      filter.endDate = dateRange.end.toString() as unknown as CalendarDate;
    }

    if (excludeRecurring) {
      filter.recurringTransactionId = null;
    }

    if (debouncedQuery) {
      const searchTypes = new Set<TransactionSearchType>();
      const elements = [primaryElement, secondaryElement];
      for (const element of elements) {
        if (element === TxnTableElementType.Merchant) {
          searchTypes.add(TransactionSearchType.Merchant);
        } else if (element === TxnTableElementType.Account) {
          searchTypes.add(TransactionSearchType.FinancialAccount);
        }
      }

      filter.search = {
        query: debouncedQuery,
        types: [...searchTypes],
      };
    }

    return filter;
  }, [
    selectedAccountIds,
    merchantIds,
    selectedTransactionAmountTypes,
    dateRange,
    excludeRecurring,
    debouncedQuery,
    primaryElement,
    secondaryElement,
  ]);

  const { data, loading, error, fetchMore } = useQuery<
    GetTransactionsForTableQuery,
    GetTransactionsForTableQueryVariables
  >(GET_TRANSACTIONS_FOR_TABLE, {
    variables: {
      pagination: {
        cursor: undefined,
        limit: PAGE_SIZE,
        direction: PaginationDirection.Forward,
      },
      filters,
      sortKey: TransactionSortKeys.Date,
    },
    notifyOnNetworkStatusChange: true,
  });

  // Apollo cache automatically merges transactions, so we can read directly from data
  const transactions = React.useMemo(() => data?.getTransactionsForUser?.edges?.map((edge) => edge.node) ?? [], [data]);
  const hasNextPage = data?.getTransactionsForUser?.pageInfo?.hasNextPage ?? false;
  // Check if we're loading more by seeing if we already have transactions
  const isLoadingMore = loading && transactions.length > 0;

  const handleDateRangeChange = React.useCallback(
    (range: CalendarDateRange) => {
      onDateRangeChange?.(range);
    },
    [onDateRangeChange]
  );

  const emptyStateProps = useHasTransactionsForEmptyState({
    filters,
    isLoading: loading,
    hasRows: transactions.length > 0,
    currentDateRange: dateRange,
    onViewTransactions: handleDateRangeChange,
  });

  const handleAccountChange = React.useCallback((ids: string[]) => {
    setSelectedAccountIds(ids);
  }, []);

  const handleTransactionAmountTypeChange = React.useCallback((types: TransactionAmountType[]) => {
    setSelectedTransactionAmountTypes(types);
  }, []);

  const handleLoadMore = React.useCallback(async () => {
    const pageInfo = data?.getTransactionsForUser?.pageInfo;
    if (pageInfo?.endCursor) {
      await fetchMore({
        variables: {
          pagination: {
            cursor: pageInfo.endCursor,
            limit: PAGE_SIZE,
            direction: PaginationDirection.Forward,
          },
          filters,
          sortKey: TransactionSortKeys.Date,
        },
      });
    }
  }, [data, fetchMore, filters]);

  const hasActiveUserFilters =
    selectedTransactionAmountTypes.length > 0 ||
    debouncedQuery !== '' ||
    (financialAccountIds === undefined && selectedAccountIds.length > 0);

  if (hideEmpty && transactions.length === 0 && !loading && !hasActiveUserFilters) {
    return null;
  }

  return (
    <BaseTransactionsTable
      transactions={transactions}
      primaryElement={primaryElement}
      secondaryElement={secondaryElement}
      allowedFilters={allowedFilters}
      dateRange={dateRange}
      selectedAccountIds={selectedAccountIds}
      onAccountChange={handleAccountChange}
      selectedTransactionAmountTypes={selectedTransactionAmountTypes}
      onTransactionAmountTypeChange={handleTransactionAmountTypeChange}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      isLoadingInitial={loading && transactions.length === 0}
      isLoadingMore={isLoadingMore}
      hasError={Boolean(error)}
      hasNextPage={hasNextPage}
      onLoadMore={handleLoadMore}
      emptyStateProps={emptyStateProps}
    />
  );
};

export default TransactionsTable;
